import { Button, message, Modal, Tooltip } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import CIQDatePicker from "components/custom-date-picker";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import {
  CalendarOutlined,
  CloseCircleFilled,
  ExclamationCircleOutlined,
  InfoCircleFilled
} from "@ant-design/icons";
import { MaterialCiqIcon } from "components/svg-icons";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { SidepanelStyles } from "../../constants";

interface StepCardProps {
  item: WorkflowItem;
  materials: WorkflowItem[];
  updatedItems: {
    [key: string]: {
      feature_instance_id: string;
      workflow_instance_id: string;
      id: string;
      actualDate: string;
    };
  };
}

interface SpecSection {
  id: string;
  name: string;
  number: string;
}

interface Task {
  task_id: string;
  task_name: string;
  reverse_dependency: boolean;
  offset_duration: number | null;
  linked_offset_date: string | null;
  activity_id: string;
  critical: boolean;
  total_slack: number;
  is_critical: boolean;
  total_slack_computed: number | null;
  progress: number;
  start_date: string;
  end_date: string;
  linked_to_end_date: boolean;
  actual_start_date: string;
  actual_finish_date: string;
}

interface Milestone {
  id: string;
  sequence_no: number;
  name: string;
  actual: string | null;
  planned: string;
  projected: string;
  offset_id: string;
  offset_name: string;
  offset_duration: number;
}

interface DateBlock {
  id: string;
  feature_type: number;
  feature_id: string;
  final_deadline_date: string;
  projected_last_milestone_date: string;
  milestones: Milestone[];
}

interface LinkedItem {
  id: string;
  feature_type: number;
  sequence_id: number;
  name: string;
  milestone_state: string;
  risk_level: string;
  spec_section: SpecSection[];
  workflow_status: string;
  milestones: any[];
  deleted_from_source: boolean;
  revision: string | null;
  type: string;
}

interface Template {
  id: string;
  name: string;
}

interface WorkflowItem {
  id: string;
  sequence_id: number;
  number: string | null;
  title: string;
  description: string | null;
  workflow_status: string;
  are_predecessors_complete: boolean;
  tag_id: string | null;
  quantity_unit_id: string | null;
  size_unit_id: string | null;
  part_number: string | null;
  serial_number: string | null;
  warranty: string | null;
  manufacturer: string | null;
  material_size: string | null;
  quantity: number | null;
  unit_price: number | null;
  total_price: number;
  delta: number | null;
  assignee_unregistered: string | null;
  tag_name: string | null;
  import_log_id: string | null;
  place_id: string | null;
  purchase_order: string | null;
  next_planned_deadline: string;
  next_action: string;
  due_date: string;
  risk_level: string;
  milestone_state: string;
  material_released_early: string | null;
  pre_WF_start_float: number;
  effective_float: number;
  lead_time: number | null;
  spec_section: SpecSection[];
  linked_items: LinkedItem[];
  tasks: Task[];
  date_block: DateBlock;
  extraction_datum: Record<string, any>;
  locations: any[];
  template: Template;
  current_milestone_id: string;
  current_milestone_name: string;
  previous_milestone_date: string | null;
  first_milestone_id: string;
  first_milestone_name: string;
  materials: LinkedItem[];
}

interface CategorizedItems {
  completed: WorkflowItem[];
  predecessorsIncomplete: WorkflowItem[];
  remaining: GroupedRemaining;
}

interface DateItemProps {
  item: WorkflowItem;
  onDateChange: (itemId: string, value: string) => void;
  value?: any;
  isInvalid?: boolean;
}

interface GroupedRemaining {
  [templateName: string]: {
    [milestoneName: string]: WorkflowItem[];
  };
}

function truncateWithEllipsis(input: string, maxLength: number = 20): string {
  if (!input) return input;
  return input.length > maxLength
    ? `${input.substring(0, maxLength)}...`
    : input;
}

const tooltipContent = (items: WorkflowItem[]) => (
  <div className="flex flex-col gap-2 p-1">
    {items.map((item) => (
      <div
        key={item.id}
        style={{
          border: "1px solid var(--ciq-gray-250)",
          borderRadius: "6px",
          padding: "8px 12px",
          background: "white",
          fontSize: "14px",
          color: "var(--ciq-gray-900)",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          minWidth: "200px",
          maxWidth: "250px",
          cursor: "pointer",
          transition: "background-color 0.2s"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "var(--ciq-neutral-25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "white";
        }}
      >
        <Tooltip title={item.title}>{item.title}</Tooltip>
      </div>
    ))}
  </div>
);

function DateItem({ item, onDateChange, value, isInvalid }: DateItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDateChange = (date: any) => {
    onDateChange(item.id, date);
  };

  const handleCalendarClick = () => {
    setIsExpanded(true);
  };

  const maxTitleLength = !isExpanded && !value ? 35 : 20;
  const shouldShowDatePicker = isExpanded || value;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 20px"
      }}
    >
      <Tooltip title={item.title}>
        <span style={{ color: "var(--ciq-gray-700)", fontSize: "14px" }}>
          {truncateWithEllipsis(item.title, maxTitleLength)}
        </span>
      </Tooltip>

      {shouldShowDatePicker ? (
        <CIQDatePicker
          className={`w-[140px] ${isInvalid ? "border-red-500" : ""}`}
          placeholder="Select date"
          format={DATE_FORMAT_MMDDYYYY}
          value={value}
          onChange={handleDateChange}
          disabledDate={(current) =>
            current && current.isAfter(moment().endOf("day"))
          }
          style={isInvalid ? { borderColor: "var(--ciq-error-500)" } : {}}
          onBlur={() => {
            if (!value) {
              setIsExpanded(false);
            }
          }}
        />
      ) : (
        <CalendarOutlined
          onClick={handleCalendarClick}
          style={{
            fontSize: "16px",
            color: "var(--ciq-gray-700)",
            cursor: "pointer",
            padding: "8px",
            width: "25px",
            textAlign: "center"
          }}
        />
      )}
    </div>
  );
}

function StepCard({
  item,
  materials,
  updatedItems,
  onDateChange
}: StepCardProps & { onDateChange: (itemId: string, value: string) => void }) {
  return (
    <div
      style={{
        marginBottom: "16px",
        border: "1px solid var(--ciq-gray-250)",
        borderRadius: "8px",
        backgroundColor: "white"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          backgroundColor: "var(--ciq-gray-600)",
          opacity: "75%",
          padding: "16px"
        }}
      >
        <div
          style={{
            width: "24px",
            height: "24px",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "bold",
            marginRight: "12px"
          }}
        >
          <div
            className="flex items-center justify-center rounded"
            style={{
              padding: 3,
              backgroundColor: "var(--ciq-blue-250)",
              color: "black"
            }}
          >
            <MaterialCiqIcon
              size={20}
              className="fill-ciq-gray-850 pr-[5px]"
              opacity="1"
            />
            {materials.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "14px", color: "var(--ciq-gray-700)" }}>
            Set Actual Date for
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "var(--ciq-blue-500)"
            }}
          >
            {item.current_milestone_name}
          </div>
        </div>
      </div>

      <div>
        {materials.map((value) => {
          const hasDate = updatedItems[value.id]?.actualDate;
          const isInvalid =
            hasDate && value.previous_milestone_date
              ? !moment(updatedItems[value.id].actualDate).isSameOrAfter(
                  moment(value.previous_milestone_date),
                  "day"
                )
              : false;

          return (
            <DateItem
              key={value.id}
              item={value}
              onDateChange={onDateChange}
              value={
                updatedItems[value.id]?.actualDate
                  ? moment(updatedItems[value.id].actualDate)
                  : null
              }
              isInvalid={isInvalid}
            />
          );
        })}
      </div>
    </div>
  );
}

function BulkFeatureEditMaterialStatus(props: any) {
  const { onUpdateDone, selectedRows, showEdit, setShowEdit } = props;
  const [categorizedItems, setCategorizedItems] = useState<CategorizedItems>({
    completed: [],
    predecessorsIncomplete: [],
    remaining: {}
  });
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [showBothSections, setShowBothSections] = useState(false);
  const [showOneSection, setShowOneSection] = useState(false);
  const [reviewEditExpanded, setReviewEditExpanded] = useState(false);
  const [mainDate, setMainDate] = useState<string | null>(null);
  const [overrideCount, setOverrideCount] = useState(0);
  const [updatedItems, setUpdatedItems] = useState<{
    [key: string]: {
      feature_instance_id: string;
      workflow_instance_id: string;
      id: string;
      actualDate: string;
    };
  }>({});

  const { gqlClientForProject, calHolidaysWeekends }: TProjectContext =
    useContext(ProjectContext);
  const [mutationApplyWorkflowInstanceDataChanges] = useCIQMutation(
    APPLY_WORKFLOW_MUTATION,
    {
      client: gqlClientForProject
    }
  );

  const findPreviousValidDate = useCallback(
    (startDate: moment.Moment): moment.Moment => {
      const holidays = calHolidaysWeekends?.holidays;
      const weekends = calHolidaysWeekends?.weekends;
      let currentDate = startDate.clone();

      const isDisabledDate = (date: moment.Moment) => {
        let disableHolidaysResult = false;
        let disableWeekendsResult = false;

        if (holidays) {
          disableHolidaysResult = !!holidays.find((hld: any) =>
            date.isSame(moment(hld?.date), "D")
          );
        }

        if (weekends) {
          disableWeekendsResult = weekends.some((wkend: number) => {
            return date.day() === wkend;
          });
        }

        return disableHolidaysResult || disableWeekendsResult;
      };

      let attempts = 0;
      while (isDisabledDate(currentDate) && attempts < 10) {
        currentDate = currentDate.subtract(1, "day");
        attempts += 1;
      }

      return currentDate;
    },
    [calHolidaysWeekends?.holidays, calHolidaysWeekends?.weekends]
  );

  const isDateValid = (
    selectedDate: string,
    previousMilestoneDate: string | null
  ): boolean => {
    if (!previousMilestoneDate || !selectedDate) return true;
    return moment(selectedDate).isSameOrAfter(
      moment(previousMilestoneDate),
      "day"
    );
  };

  const handleMainDateChange = (date: any) => {
    const validDate = findPreviousValidDate(moment(date));
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);
    setMainDate(dateValue);
    const updated: {
      [key: string]: {
        feature_instance_id: string;
        workflow_instance_id: string;
        id: string;
        actualDate: string;
      };
    } = {};

    Object.values(categorizedItems.remaining).forEach((milestones) => {
      Object.values(milestones).forEach((items) => {
        items.forEach((item: WorkflowItem) => {
          updated[item.id] = {
            feature_instance_id: item.id,
            workflow_instance_id: item.date_block.id,
            id: item.current_milestone_id,
            actualDate: dateValue
          };
        });
      });
    });

    setUpdatedItems((prev) => {
      const newState = { ...prev, ...updated };

      const overrides = Object.keys(newState).filter(
        (id) => dateValue && newState[id].actualDate !== dateValue
      ).length;
      setOverrideCount(overrides);

      return newState;
    });
  };

  const handleIndividualDateChange = (itemId: string, date: any) => {
    const validDate = findPreviousValidDate(moment(date));
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);

    let targetItem: WorkflowItem | undefined;
    Object.values(categorizedItems.remaining).forEach((milestones) => {
      Object.values(milestones).forEach((items) => {
        const foundItem = items.find(
          (item: WorkflowItem) => item.id === itemId
        );
        if (foundItem) {
          targetItem = foundItem;
        }
      });
    });

    setUpdatedItems((prev) => {
      const newUpdated = { ...prev };

      newUpdated[itemId] = {
        feature_instance_id: targetItem!.id,
        workflow_instance_id: targetItem!.date_block.id,
        id: targetItem!.current_milestone_id,
        actualDate: dateValue
      };

      const overrides = Object.keys(newUpdated).filter(
        (id) => mainDate && newUpdated[id].actualDate !== mainDate
      ).length;
      setOverrideCount(overrides);

      return newUpdated;
    });
  };

  const categorizeItems = (items: WorkflowItem[]): CategorizedItems => {
    const completed: WorkflowItem[] = [];
    const predecessorsIncomplete: WorkflowItem[] = [];
    const remaining: GroupedRemaining = {};

    items.forEach((item) => {
      if (item.workflow_status === "WF_COMPLETED") {
        completed.push(item);
      } else if (
        (item.linked_items.length > 0 || item.materials.length > 0) &&
        item.are_predecessors_complete === false &&
        item.first_milestone_id === item.current_milestone_id
      ) {
        predecessorsIncomplete.push(item);
      } else {
        const templateName = item.template.name;
        const milestoneName = item.current_milestone_name;

        if (!remaining[templateName]) {
          remaining[templateName] = {};
        }

        if (!remaining[templateName][milestoneName]) {
          remaining[templateName][milestoneName] = [];
        }

        remaining[templateName][milestoneName].push(item);
      }
    });

    if (completed.length > 0 && predecessorsIncomplete.length > 0) {
      setShowBothSections(true);
    } else if (completed.length > 0 || predecessorsIncomplete.length > 0) {
      setShowOneSection(true);
    } else {
      setShowBothSections(false);
      setShowOneSection(false);
    }

    return {
      completed,
      predecessorsIncomplete,
      remaining
    };
  };

  useEffect(() => {
    const categorized = categorizeItems(selectedRows);
    setCategorizedItems(categorized);
  }, [selectedRows]);

  useEffect(() => {
    const today = DateUtils.dateTimeObj();
    const validDate = findPreviousValidDate(today);
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);
    setMainDate(dateValue);

    const updated: {
      [key: string]: {
        feature_instance_id: string;
        workflow_instance_id: string;
        id: string;
        actualDate: string;
      };
    } = {};

    Object.values(categorizedItems.remaining).forEach((milestones) => {
      Object.values(milestones).forEach((items) => {
        items.forEach((item: WorkflowItem) => {
          updated[item.id] = {
            feature_instance_id: item.id,
            workflow_instance_id: item.date_block.id,
            id: item.current_milestone_id,
            actualDate: dateValue
          };
        });
      });
    });
    // console.log("Initial Updated", updated);
    setUpdatedItems(updated);
  }, [categorizedItems.remaining, findPreviousValidDate]);

  // console.log("selectedRows", selectedRows);
  // console.log("categorizedItems", categorizedItems);

  const hasAnyDateEntry = Object.keys(updatedItems).length > 0;

  const calculateInvalidCount = () => {
    let invalidCount = 0;
    Object.entries(updatedItems).forEach(([itemId, data]) => {
      let targetItem: WorkflowItem | undefined;
      Object.values(categorizedItems.remaining).forEach((milestones) => {
        Object.values(milestones).forEach((items) => {
          const foundItem = items.find(
            (item: WorkflowItem) => item.id === itemId
          );
          if (foundItem) {
            targetItem = foundItem;
          }
        });
      });

      if (
        targetItem &&
        !isDateValid(data.actualDate, targetItem.previous_milestone_date)
      ) {
        invalidCount += 1;
      }
    });
    return invalidCount;
  };

  const currentInvalidCount = calculateInvalidCount();
  const hasValidEntries = hasAnyDateEntry && currentInvalidCount === 0;

  useEffect(() => {
    if (currentInvalidCount > 0) {
      setReviewEditExpanded(true);
    }
  }, [currentInvalidCount]);

  const onConfirm = async () => {
    setIsConfirmDisabled(true);
    const payload = {
      updates: Object.entries(updatedItems).map(([itemId, data]) => ({
        feature_instance_id: itemId,
        workflow_instance_id: data.workflow_instance_id,
        milestones: [
          {
            id: data.id,
            actual_date: data.actualDate
          }
        ]
      }))
    };

    // console.log("Payload", payload);
    const response = await mutationApplyWorkflowInstanceDataChanges({
      variables: {
        input: payload.updates,
        metadata: {
          request_source: "LOG_PAGE"
        }
      }
    });

    if (response.success) {
      setShowEdit(null);
      message.success("Updated successfully");
      setTimeout(() => {
        onUpdateDone();
      }, 0);
    }
    setIsConfirmDisabled(false);
  };

  const toggleReviewEdit = () => {
    setReviewEditExpanded((prev) => !prev);
  };

  return (
    <Modal
      title={[
        <div data-testid="bulkfeatureeditmaterialstatus-modal">
          <span>Bulk Edit Status</span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal">
            {selectedRows.length} Selected Materials
          </span>
        </div>
      ]}
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      footer={[
        <div className="flex w-full space-x-3 justify-end" key={103}>
          <Button
            onClick={() => {
              onUpdateDone();
            }}
            key={104}
            disabled={false}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!hasValidEntries || isConfirmDisabled}
            loading={false}
            onClick={onConfirm}
            key={105}
            data-testid="apply-button"
          >
            Confirm
          </Button>
        </div>
      ]}
      open={showEdit}
      onCancel={() => {
        setShowEdit(false);
        onUpdateDone();
      }}
      destroyOnClose
      data-testid="bulkfeatureeditmaterialstatus-modal-root"
    >
      <div
        className="body-content-wrapper"
        data-testid="bulkfeatureeditmaterialstatus-body"
      >
        {(showOneSection || showBothSections) && (
          <div
            style={{
              backgroundColor: "var(--ciq-blue-100)",
              border: "1px solid var(--ciq-blue-300)",
              borderRadius: "6px",
              padding: "12px 10px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "flex-start"
            }}
            data-testid="bulkfeatureeditmaterialstatus-info-section"
          >
            <div>
              <InfoCircleFilled className="text-ciq-blue-600 pr-[5px]" />
            </div>
            {showBothSections && (
              <div>
                <div
                  style={{
                    color: "var(--ciq-blue-500)",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    paddingTop: "2px"
                  }}
                >
                  Status of the following will be affected:
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "16px",
                    color: "var(--ciq-gray-700)",
                    fontSize: "12px",
                    lineHeight: "1.5"
                  }}
                >
                  <li data-testid="bulkfeatureeditmaterialstatus-completed-list">
                    <Tooltip title={tooltipContent(categorizedItems.completed)}>
                      <strong className="underline">
                        {categorizedItems.completed.length}
                      </strong>{" "}
                    </Tooltip>
                    Completed Material
                    {categorizedItems.completed.length !== 1 ? "s" : ""} will
                    not be updated.
                  </li>
                  <li data-testid="bulkfeatureeditmaterialstatus-predecessors-list">
                    <Tooltip
                      title={tooltipContent(
                        categorizedItems.predecessorsIncomplete
                      )}
                    >
                      <strong className="underline">
                        {categorizedItems.predecessorsIncomplete.length}
                      </strong>{" "}
                    </Tooltip>
                    Material
                    {categorizedItems.predecessorsIncomplete.length !== 1
                      ? "s"
                      : ""}{" "}
                    with linked to Predecessors which are not closed will not be
                    updated.
                  </li>
                </ul>
              </div>
            )}
            {showOneSection &&
              (categorizedItems.completed.length > 0 ? (
                <div data-testid="bulkfeatureeditmaterialstatus-completed-section">
                  <Tooltip title={tooltipContent(categorizedItems.completed)}>
                    <strong className="underline">
                      {categorizedItems.completed.length}
                    </strong>{" "}
                  </Tooltip>
                  Completed Materials will not be updated.
                </div>
              ) : (
                <div data-testid="bulkfeatureeditmaterialstatus-predecessors-section">
                  <Tooltip
                    title={tooltipContent(
                      categorizedItems.predecessorsIncomplete
                    )}
                  >
                    <strong className="underline">
                      {categorizedItems.predecessorsIncomplete.length}
                    </strong>{" "}
                  </Tooltip>
                  Materials with linked to Predecessors which are not closed
                  will not be updated.
                </div>
              ))}
          </div>
        )}
        <div
          className="mb-1 font-semibold"
          data-testid="set-actual-date-next-milestone"
        >
          <span
            className="tracking-wide"
            data-testid="selected-materials-count-text"
          >
            Set Actual Date of Next Milestone
          </span>
        </div>
        <div className="mb-3 text-xs">
          <span style={{ opacity: "45%" }}>
            Date entered applies to all selected Materials. You can change the
            date of individual Materials in the ‘Review & Edit’ section.
          </span>
        </div>
        <div
          className="w-[95%] pb-[15px]"
          data-testid="bulkfeatureeditmaterialstatus-main-datepicker-section"
        >
          <span className="mb-3 text-xs" style={{ opacity: "85%" }}>
            Milestone Completed On
          </span>
          <CIQDatePicker
            className="w-full"
            placeholder="Select date"
            format={DATE_FORMAT_MMDDYYYY}
            value={mainDate ? moment(mainDate) : null}
            onChange={(date: any) => handleMainDateChange(date)}
            placement="topLeft"
            disabledDate={(current) =>
              current && current.isAfter(moment().endOf("day"))
            }
            data-testid="bulkfeatureeditmaterialstatus-main-datepicker"
          />
          {overrideCount > 0 && (
            <span
              style={{ fontSize: "12px" }}
              data-testid="bulkfeatureeditmaterialstatus-override-count"
            >
              <strong>{overrideCount}</strong>{" "}
              <span style={{ opacity: "45%" }}>
                Material
                {overrideCount !== 1 ? "s" : ""} have different dates
              </span>
            </span>
          )}
          {currentInvalidCount > 0 && (
            <div
              style={{
                backgroundColor: "var(--ciq-red-100)",
                border: "1px solid var(--ciq-red-300)",
                borderRadius: "6px",
                padding: "12px",
                marginTop: "8px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px"
              }}
              data-testid="bulkfeatureeditmaterialstatus-invalid-warning"
            >
              <CloseCircleFilled
                className="text-red-500 pt-1 hover:text-red-500"
                style={{ width: "14px", height: "14px" }}
              />
              <div style={{ flex: 1 }}>
                <span
                  style={{ fontSize: "12px", color: "var(--ciq-gray-800)" }}
                >
                  This date cannot be added to{" "}
                  <strong>
                    {currentInvalidCount} Material
                    {currentInvalidCount !== 1 ? "s" : ""}
                  </strong>{" "}
                  as it is earlier than its previous Milestone&apos;s Actual
                  date.
                  <br />
                  Please set another date for them below.
                </span>
              </div>
            </div>
          )}
        </div>
        <hr style={{ opacity: "45%" }} />
        <div className="pt-[10px]">
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              onClick={toggleReviewEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleReviewEdit();
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={reviewEditExpanded}
              aria-label="Toggle review and edit section"
              data-testid="bulkfeatureeditmaterialstatus-clickable-div"
            >
              <div
                style={{
                  margin: 0,
                  fontSize: "14px",
                  flex: 1
                }}
              >
                <p
                  className="mb-1 font-semibold"
                  data-testid="bulkfeatureeditmaterialstatus-review-edit-title"
                >
                  Review & Edit
                </p>

                <p
                  style={{
                    opacity: "45%",
                    fontSize: "12px",
                    margin: "0 0 16px 0"
                  }}
                  data-testid="bulkfeatureeditmaterialstatus-review-edit-desc"
                >
                  Edit or Set a different Actual Date for each Material.
                </p>
              </div>
              {currentInvalidCount > 0 && (
                <ExclamationCircleOutlined
                  className="text-red-500 pt-1 hover:text-red-500 mb-[28px]"
                  style={{ width: "14px", height: "14px" }}
                  data-testid="bulkfeatureeditmaterialstatus-review-edit-warning"
                />
              )}
              <i
                className="fas fa-chevron-up"
                style={{
                  marginLeft: "8px",
                  marginBottom: "20px",
                  transform: reviewEditExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.3s"
                }}
                data-testid="bulkfeatureeditmaterialstatus-chevron"
              />
            </div>

            {reviewEditExpanded && (
              <div data-testid="bulkfeatureeditmaterialstatus-review-edit-section">
                {Object.entries(categorizedItems.remaining).map(
                  ([templateName, milestones]) => (
                    <div
                      key={templateName}
                      data-testid={`bulkfeatureeditmaterialstatus-template-${templateName}`}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          margin: "0 0 10px 0"
                        }}
                        data-testid="bulkfeatureeditmaterialstatus-template-title"
                      >
                        {templateName}
                      </div>
                      <ul className="pl-[0px]">
                        {Object.entries(milestones).map(
                          ([milestoneName, items]) => (
                            <StepCard
                              key={milestoneName}
                              item={items[0]}
                              materials={items}
                              updatedItems={updatedItems}
                              onDateChange={handleIndividualDateChange}
                            />
                          )
                        )}
                      </ul>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default BulkFeatureEditMaterialStatus;
